import { z } from "@hono/zod-openapi";
import { vehicleAttachedV1 } from "../../vehicleAssignment/vehicleAttached";
import { vehicleDetachedV1 } from "../../vehicleAssignment/vehicleDetached";
import type { TmsEventBodyOf, TmsEventKind } from "../../taxonomy";

/**
 * One driver taking or leaving a seat on one unit, at one instant.
 *
 * The stream speaks Storyboard's model — attached with a seat, or detached — and nothing above the adapter learns how
 * a vendor represents a team. A vendor's logins, logouts, and seat swaps are crunched into these two kinds in its
 * own strategy, and the assignment intervals a client folds from them are a pure function of the stream.
 *
 * The kinds it may state are declared here once, and the registry holds the subject to them.
 * `satisfies` rather than an annotation, so each name stays a literal and a typo is still a compile error.
 */
export const vehicleDriverAssignmentTransitionKinds = [
  "vehicleAttached",
  "vehicleDetached",
] as const satisfies readonly TmsEventKind[];

export type VehicleDriverAssignmentTransitionKind = (typeof vehicleDriverAssignmentTransitionKinds)[number];

/**
 * The type of `tms_event.vehicle_driver_assignment_transition.facts`: any of this subject's kinds, at any version.
 *
 * What the generated Drizzle column's `.$type<…>()` names, and what the repository parses every written and read
 * value against — so the column's compile-time type and its runtime contents answer to the same schema.
 *
 * Written out one arm at a time rather than derived from the kind list above. Every version a column may hold
 * is named here, so shipping a second version of a kind is a visible edit in this file rather than a silent
 * widening, and forgetting it fails the first time the writer emits that version. When a kind does reach two,
 * its arms nest in a union on `version`: zod keeps only the first arm when a discriminator value repeats, so
 * listing both flat would quietly parse one version's documents against the other's schema.
 */
export const VehicleDriverAssignmentTransitionFactsSchema = z.discriminatedUnion("kind", [
  vehicleAttachedV1.facts,
  vehicleDetachedV1.facts,
]);

export type VehicleDriverAssignmentTransitionFacts = z.infer<typeof VehicleDriverAssignmentTransitionFactsSchema>;

/**
 * What a strategy produces for this subject: references and facts, without the base the writer supplies.
 */
export type VehicleDriverAssignmentTransitionBody = TmsEventBodyOf<VehicleDriverAssignmentTransitionKind>;
